import { AccountType } from '@repo/types/enums/accountType';
import { UserLvl } from '@repo/types/enums/userLvl';
import { ReactElement } from 'react';
import { Navigate, Outlet } from 'react-router';

import { Loading } from '@/components/ui/loading';
import { useProtectedRoute } from '@/hooks/protected-route';

/**
 * Props for the ProtectedRoutes component.
 *
 * - `children`: Optional child component to render. If provided, takes precedence over Outlet.
 * - `userLvls`: The required user level(s) to access this route. Can be a single level or an array of levels.
 * - `accountTypes`: The required account type(s). Defaults to regular users, which keeps
 *   parent accounts out of routes that predate them — parents share `UserLvl.STUDENT`
 *   with students, so the level alone does not distinguish them.
 * - `redirectPath`: The path to redirect unauthorized users to. Defaults to '/notAllowed'.
 * - `replace`: Whether to replace the history entry when redirecting. Defaults to false.
 */
export interface ProtectedRoutesProps {
  children?: ReactElement;
  userLvls: UserLvl | UserLvl[];
  accountTypes?: AccountType | AccountType[];
  redirectPath?: string;
  replace?: boolean;
}

/**
 * ProtectedRoutes Component
 *
 * A route guard component that protects routes based on user authorization levels.
 * It validates the current user's level against required level(s) and either allows
 * access, shows a loading state, or redirects to a specified path.
 *
 * @example
 * // Protect a route for admin users only
 * <ProtectedRoutes userLvls={UserLvl.ADMIN}>
 *   <AdminDashboard />
 * </ProtectedRoutes>
 *
 * @example
 * // Protect a route for multiple user levels
 * <ProtectedRoutes userLvls={[UserLvl.ADMIN, UserLvl.MODERATOR]} redirectPath="/login">
 *   <Dashboard />
 * </ProtectedRoutes>
 *
 * @example
 * // Use with nested routes (Outlet pattern)
 * <ProtectedRoutes userLvls={UserLvl.USER} />
 */
export function ProtectedRoutes(protectedRoutesProps: ProtectedRoutesProps) {
  const { children, userLvls, accountTypes = AccountType.User, redirectPath = '/notAllowed', replace = false } = protectedRoutesProps;

  const { allow, isLoading } = useProtectedRoute(userLvls, accountTypes);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loading />
      </div>
    );
  } else if (!allow) {
    return <Navigate to={redirectPath} replace={replace} />;
  } else if (children) {
    return children;
  }
  return <Outlet />;
}
